import { Link, LinkProps } from 'react-router-dom';
import { classNames } from '@cotera/client/app/components/utils';

export const CoteraLink: React.FC<
  LinkProps & React.RefAttributes<HTMLAnchorElement>
> = ({ className = '', ...props }) => {
  return (
    <Link
      className={classNames(
        className,
        'text-primary-text hover:underline font-medium'
      )}
      {...props}
    />
  );
};

/**
 * A secondary way out of the thing you are looking at: "Nested in <workflow>",
 * "Open details", and similar.
 *
 * Reads as body text with a dashed underline that firms up on hover, rather
 * than as a coloured link. These sit inside dense surfaces next to the content
 * that matters, so a primary-coloured link pulls the eye away from it; the
 * dashed rule marks the affordance without competing.
 *
 * Renders an anchor when given `to`, otherwise a button, so a route and a
 * callback get the same treatment instead of two look-alike implementations.
 */
const SUBTLE_LINK_CLASS =
  'underline decoration-dashed underline-offset-2 transition-colors hover:text-foreground hover:decoration-solid';

type SubtleLinkProps = {
  className?: string;
  children: React.ReactNode;
} & (
  | { to: LinkProps['to']; onClick?: never }
  | { to?: never; onClick: (event: React.MouseEvent) => void }
);

export const SubtleLink: React.FC<SubtleLinkProps> = ({
  className,
  children,
  ...rest
}) => {
  if (rest.to !== undefined) {
    return (
      <Link to={rest.to} className={classNames(SUBTLE_LINK_CLASS, className)}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={rest.onClick}
      className={classNames(SUBTLE_LINK_CLASS, className)}
    >
      {children}
    </button>
  );
};
